import React, { useMemo, useCallback, useState, useRef } from 'react';
import { Modal, Button as ButtonLib, Alert, notification } from 'antd';
import { FilterSelect, FilterSelectOption } from '../inputs/filter-select';
import { useServerSideDatatable } from '../serverside-datatable';
import { Sprite } from '../sprite';
import Form from '../Form';
import { FieldType } from '../../types';
import { useAuthStore } from '../../store/auth-store';
import { axiosInstance } from '../../services/axios-instance';
import { errorMessage } from '../../utils';
import { RefFormHandler } from '../../pages/orders/details';

interface Props {}

export const OrderFilterer: React.FC<Props> = () => {
  const {
    searchBarPlaceholder,
    currentPage,
    setSearchTerm,
    setFilterByValue,
    filterByValue,
    setFilterByField,
    query,
  } = useServerSideDatatable();

  const statusOptions = useMemo<FilterSelectOption[]>(
    () =>
      [
        'New',
        'Order Approved',
        'Order Rejected',
        'Kit Shipped',
        'Specimen Received',
        'Recollection Required',
        'Completed',
        'Release Approved',
        'Release Rejected',
      ].map(value => ({
        value: value.toUpperCase(),
        label: value,
      })),
    [],
  );

  const [uploadState, setUploadState] = useState<{
    success?: any;
    error?: string;
    isLoading?: boolean;
  }>({});

  const [isOpen, setIsOpen] = useState(false);

  const formRef = useRef<RefFormHandler>(null);

  const onClose = useCallback(() => {
    setUploadState({});
    setIsOpen(false);
  }, []);

  const [contextHolder] = notification.useNotification();

  const { refreshSession } = useAuthStore();

  const reset = useCallback(async () => {
    if (formRef.current && typeof formRef.current.reset === 'function') {
      formRef.current.reset();
    }
    setUploadState({});
    refreshSession();
  }, []);

  const onSubmitCapture = useCallback(
    async values => {
      setUploadState(e => ({ ...e, isLoading: true }));
      const { file } = values;
      if (!values.file?.[0]) {
        setUploadState(e => ({ ...e, isLoading: false }));
        return;
      }
      const formData = new FormData();
      formData.append('file', file[0]);
      try {
        const { data } = await axiosInstance({
          method: 'POST',
          url: '/lab-results/bulkUploadCsv',
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        setUploadState({
          success: (
            <div>
              <p>Upload of &apos;{file[0].name}&apos; was successful. </p>
              {data.orders.map(id => (
                <a
                  key={id}
                  href={`/orders/${id}`}
                  target="_blank"
                  style={{ marginRight: '10px', textDecoration: 'underline' }}
                  rel="noreferrer">
                  {`Order ${id}`}
                </a>
              ))}
            </div>
          ),
        });
      } catch (err) {
        setUploadState({ error: errorMessage(err) });
        refreshSession();
        throw err;
      } finally {
        setUploadState(e => ({ ...e, isLoading: false }));
      }
    },
    [refreshSession],
  );

  return (
    <div className="flex flex-row mb-4">
      <div className="flex-1">
        <div className="relative">
          <div className="absolute top-0 pl-[14.5px] h-full flex flex-col justify-center z-[2] pointer-events-none">
            <Sprite
              name="icon-search"
              className="w-[20px] h-[20px] text-gray fill-gray"
            />
          </div>
          <input
            type="text"
            className="border border-gray-medium rounded-md pl-[40px] py-2 pr-2 placeholder:text-gray relative z-[1] w-full bg-white"
            placeholder={searchBarPlaceholder}
            onChange={event => setSearchTerm(event.currentTarget.value)}
          />
        </div>
      </div>
      <div className="flex-1 flex flex-row justify-end pl-16">
        <div className="flex-1 flex flex-row justify-end">
          <div className="w-auto">
            <FilterSelect
              currentValue={filterByValue}
              placeholder="All"
              options={statusOptions}
              onChange={value => {
                setFilterByField(value ? 'status' : '');
                setFilterByValue(value);
              }}
              icon={
                <Sprite
                  name="icon-caret"
                  className="w-[20px] h-[20px] text-gray fill-gray"
                />
              }
            />
          </div>
        </div>
        <div className="pl-3">
          <button
            type="button"
            onClick={() => query({ page: currentPage })}
            className="px-4 py-2 border-[2px] border-white bg-white shadow-[0px_1px_1px_rgba(0,0,0,0.06)] active:shadow-none rounded-[5px] font-semibold text-blue">
            Refresh
          </button>
          <button
            type="button"
            onClick={() => {
              setIsOpen(true);
            }}
            className="ml-2 mt-[-3px] px-4 py-2 border-[2px] border-blue bg-blue text-white shadow-[0px_1px_1px_rgba(0,0,0,0.06)] active:shadow-none rounded-[5px] font-semibold inline-flex items-center space-x-2 align-middle">
            <Sprite
              name="icon-attachment"
              className="w-[20px] h-[20px] text-white fill-white"
            />
            <span>Bulk Upload</span>
          </button>

          <Modal
            open={isOpen}
            width={400}
            footer={null}
            maskClosable={false}
            closable={false}>
            <h1 className="text-[1.5rem] font-bold">Upload Bulk CSV Data</h1>
            {uploadState.error && (
              <Alert
                message={uploadState.error}
                type="error"
                className="mt-3 mb-3"
              />
            )}
            {uploadState.success && (
              <Alert
                message={uploadState.success || 'asdadadajlkdjalk '}
                type="success"
                className="mt-3 mb-3"
              />
            )}
            {!uploadState.success && (
              <Form
                ref={formRef}
                formProps={{ onSubmitCapture }}
                fields={[
                  {
                    key: 'file',
                    label:
                      'This accepts a CSV file containing multiple lab results for different people',
                    type: FieldType.File,
                    inputProps: ({ value }: { value: any }) => ({
                      fileProps: {
                        accept:
                          '.csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel',
                      },
                      buttonLabel:
                        value && value.length > 0
                          ? 'Replace CSV file'
                          : 'Upload CSV file',
                    }),
                  },
                ]}
                hasSubmitButton={false}
              />
            )}
            <div className="flex justify-end mt-2 gap-2">
              <ButtonLib onClick={onClose} loading={uploadState.isLoading}>
                Close
              </ButtonLib>
              {!uploadState.success ? (
                <ButtonLib
                  disabled={!!uploadState.success}
                  loading={uploadState.isLoading}
                  type="primary"
                  onClick={data => {
                    if (formRef) {
                      formRef.current?.onSubmit(data);
                    }
                  }}>
                  Submit
                </ButtonLib>
              ) : (
                <ButtonLib
                  type="primary"
                  onClick={data => {
                    reset();
                  }}>
                  Upload another CSV file
                </ButtonLib>
              )}
            </div>
          </Modal>
        </div>
      </div>
    </div>
  );
};
